import { createContext, use, type ReactNode } from 'react';
import type { AuthGateway } from './ports/AuthGateway';
import type { Clock } from './ports/Clock';
import type { LocationProvider } from './ports/LocationProvider';
import type { LiveStream } from './ports/LiveStream';
import type { ActivityRepository } from './ports/ActivityRepository';
import type { AdminRepository } from './ports/AdminRepository';
import type { AppUpdater } from './ports/AppUpdater';
import type { InstallPreferences } from './ports/InstallPreferences';
import type { TablePreferences } from './ports/TablePreferences';
import type { OutboxQueue, OutboxSyncDriver } from './ports/OutboxQueue';
import type { ArchiveRepository } from './ports/ArchiveRepository';
import type { BoardRepository } from './ports/BoardRepository';
import type { ClientRepository } from './ports/ClientRepository';
import type { GeoRepository } from './ports/GeoRepository';
import type { NotificationRepository } from './ports/NotificationRepository';
import type { PreferencesRepository } from './ports/PreferencesRepository';
import type { FinanceRepository } from './ports/FinanceRepository';
import type { DeviceNotifier } from './ports/DeviceNotifier';
import type { JobOrderRepository } from './ports/JobOrderRepository';
import type { KitRepository } from './ports/KitRepository';
import type { PhoneBook } from './ports/PhoneBook';
import type { PrintRepository } from './ports/PrintRepository';
import type { QrEncoder } from './ports/QrEncoder';
import type { ReportRepository } from './ports/ReportRepository';
import type { Scanner } from './ports/Scanner';

/**
 * The dependency container, section 8.4.
 *
 * A single provider composes it. In production `app/` wires the HTTP
 * implementations; in a unit test it wires fakes. Nothing below this file knows
 * which, which is the whole point of the port.
 *
 * The container is declared in `application/` rather than in `infrastructure/`
 * because the ports are declared here, and a use case must be able to ask for
 * one without reaching outward.
 */
export interface Container {
  auth: AuthGateway;
  clock: Clock;
  location: LocationProvider;
  kits: KitRepository;
  jobOrders: JobOrderRepository;
  print: PrintRepository;
  archive: ArchiveRepository;
  /** Doctors and patients. The request of 6 October 2026 (second), items 4 and 5. */
  clients: ClientRepository;
  /** Countries, regions, cities and areas, for an address. The request of 6 October 2026 (fifth), section 2. */
  geo: GeoRepository;
  /** Telephone numbers of every country, loaded when a form needs them. The same request. */
  phones: PhoneBook;
  board: BoardRepository;
  notifications: NotificationRepository;
  /**
   * What a person chose for themselves, and management's notification settings.
   * The request of 7 October 2026, sections 1 to 3.
   */
  preferences: PreferencesRepository;
  /** The money of a case, of a doctor and of the lab. The request of 8 October 2026 (fourth). */
  finance: FinanceRepository;
  /** The device's own notifications, for the ones management marked for push. */
  deviceNotifier: DeviceNotifier;
  reports: ReportRepository;
  activity: ActivityRepository;
  admin: AdminRepository;
  outbox: OutboxQueue;
  outboxSync: OutboxSyncDriver;
  installPreferences: InstallPreferences;
  tablePreferences: TablePreferences;
  appUpdater: AppUpdater;
  qr: QrEncoder;
  scanner: Scanner;
  /** The live event stream. Section 24 of the review of 2 October 2026. */
  live: LiveStream;
}

const ContainerContext = createContext<Container | null>(null);

export function ContainerProvider({
  container,
  children,
}: {
  container: Container;
  children: ReactNode;
}) {
  return <ContainerContext value={container}>{children}</ContainerContext>;
}

export function useContainer(): Container {
  const container = use(ContainerContext);
  if (container === null) {
    throw new Error('useContainer was called outside ContainerProvider');
  }
  return container;
}

export function useAuthGateway(): AuthGateway {
  return useContainer().auth;
}

export function useLiveStream(): LiveStream {
  return useContainer().live;
}

export function useClock(): Clock {
  return useContainer().clock;
}

export function useLocationProvider(): LocationProvider {
  return useContainer().location;
}

export function useKitRepository(): KitRepository {
  return useContainer().kits;
}

export function useScanner(): Scanner {
  return useContainer().scanner;
}

export function useJobOrderRepository(): JobOrderRepository {
  return useContainer().jobOrders;
}

export function usePrintRepository(): PrintRepository {
  return useContainer().print;
}

export function useQrEncoder(): QrEncoder {
  return useContainer().qr;
}

export function useArchiveRepository(): ArchiveRepository {
  return useContainer().archive;
}

export function useClientRepository(): ClientRepository {
  return useContainer().clients;
}

export function useGeoRepository(): GeoRepository {
  return useContainer().geo;
}

export function usePhoneBookPort(): PhoneBook {
  return useContainer().phones;
}

export function useBoardRepository(): BoardRepository {
  return useContainer().board;
}

export function useNotificationRepository(): NotificationRepository {
  return useContainer().notifications;
}

export function usePreferencesRepository(): PreferencesRepository {
  return useContainer().preferences;
}

export function useFinanceRepository(): FinanceRepository {
  return useContainer().finance;
}

export function useDeviceNotifier(): DeviceNotifier {
  return useContainer().deviceNotifier;
}

export function useReportRepository(): ReportRepository {
  return useContainer().reports;
}

export function useActivityRepository(): ActivityRepository {
  return useContainer().activity;
}

export function useAdminRepository(): AdminRepository {
  return useContainer().admin;
}

export function useOutbox(): OutboxQueue {
  return useContainer().outbox;
}

export function useOutboxSyncDriver(): OutboxSyncDriver {
  return useContainer().outboxSync;
}

export function useInstallPreferences(): InstallPreferences {
  return useContainer().installPreferences;
}

export function useTablePreferences(): TablePreferences {
  return useContainer().tablePreferences;
}

export function useAppUpdater(): AppUpdater {
  return useContainer().appUpdater;
}
